import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RichTextEditorComponent } from '@/app/components/shared/rich-textEditor/rich-textEditor.component';
import { NgToastService } from 'ng-angular-popup';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, BehaviorSubject } from 'rxjs';
import { User } from '@/app/interfaces/user.interface';
import { UserService } from '@/app/services/user.service';

interface ApprovalState {
  isOpen: boolean;
  isApproved: boolean;
  isLoading: boolean;
  error: string | null;
  selectedUsers: User[];
  filteredUsers: User[];
  showDropdown: boolean;
  notes: string;
}

const INITIAL_STATE: ApprovalState = {
  isOpen: false,
  isApproved: false,
  isLoading: false,
  error: null,
  selectedUsers: [],
  filteredUsers: [],
  showDropdown: false,
  notes: ''
};

@Component({
  selector: 'app-sendforapproval',
  templateUrl: './sendforapproval.component.html',
  styleUrls: ['./sendforapproval.component.css'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RichTextEditorComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendforapprovalComponent implements OnInit, OnDestroy {
  @ViewChild('dropdownContainer') private dropdownContainer!: ElementRef;
  @ViewChild('inputFieldUser') private inputField!: ElementRef;

  @Input() progress!: number; // Will receive progress from parent
  @Input() stage!: number; // Will receive progress from parent
  // @Input() progress?: number;

  private readonly destroy$ = new Subject<void>();
  private readonly state$ = new BehaviorSubject<ApprovalState>(INITIAL_STATE);
  private users: User[] = [];

  readonly userControl = new FormControl<string>('');
  resetRichTextEditor = false;

  readonly currentState$ = this.state$.asObservable();

  constructor(
    private readonly toast: NgToastService,
    private readonly projectsService: ProjectsService,
    private readonly userService: UserService
  ) {
    this.setupInputSubscription();
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    const currentState = this.state$.value;
    const newDropdownState = !currentState.showDropdown;

    this.updateState({
      showDropdown: newDropdownState
    });

    if (newDropdownState) {
      this.filterUsers(this.userControl.value || '');
    }
  }

  onClickOutside(): void {
    this.updateState({ showDropdown: false });
  }

  onSelectionChange(user: User): void {
    const currentState = this.state$.value;
    if (!currentState.selectedUsers.some(selected => selected.id === user.id)) {
      this.updateState({
        selectedUsers: [...currentState.selectedUsers, user],
        showDropdown: false
      });
      this.userControl.setValue('');
      this.filterUsers('');
    }
  }

  remove(user: User): void {
    const currentState = this.state$.value;
    this.updateState({
      selectedUsers: currentState.selectedUsers.filter(
        selected => selected.id !== user.id
      )
    });
    this.filterUsers(this.userControl.value || '');
  }


  async openSendForApproval(): Promise<void> {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.toast.danger('Issue in processing data', 'ERROR');
      return;
    }

    const data = {
      project_id: projectId,
      stage: 2
    };
  
    // for stage 2
    if (this.stage === 2) {
      try {
        // Wait for the response from the HTTP call
        const response = await this.projectsService.isStageTwoCompleted(data).toPromise();
        const result = response.data;
  
        if (!result || result !== 100) {
          this.toast.danger('Project plan is not yet filled completely', 'ERROR');
          return;
        }
  
      } catch (error) {
        console.error('Error:', error);
        this.toast.danger('An error occurred while checking the stage completion', 'ERROR');
        return;
      }
  
    }
    // for stage 1
    else if (this.progress !== 100) {
      this.toast.danger('Project stage is not yet filled completely', 'ERROR');
      return;
    }
  
    this.updateState({ isOpen: true });
    this.fetchUsers();
  }

  addNote(content: string): void {
    this.updateState({ notes: content });
  }

  async sendApprovalRequest(): Promise<void> {
    /**
     * check if all data is present and valid. if not show error toast
    */
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.toast.danger('Project is not yet created', 'ERROR');
      console.log('Project is not yet created');
      return;
    }

    const currentState = this.state$.value;

    if (currentState.selectedUsers.length === 0) {
      this.toast.danger('Please select at least one user', 'ERROR', 4000);
      return;
    }

    const transformedData = {
      // Approvers data
      project_id: projectId,
      stage: this.stage,
      data: currentState.selectedUsers.map(user => ({
        user_id: user.id,
        status: 'pending',
        designation: null,
      })),

      // TODO: these needs to be dynamic
      // Notification data
      //subject: this.state$.value.notes,
      notes: this.state$.value.notes,
      event_type: `Project Event`,
      notification_type: `in_system`,
    };

    this.updateState({ isLoading: true });

    try {
      await this.projectsService.sendApproval(transformedData)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      this.handleSuccessfulSubmission();
    } catch (error) {

      let errorMsg = 'Failed to send approval. Please try again.';
      if (error?.error?.meta?.message) {errorMsg = error?.error?.meta?.message;}
      this.toast.danger(errorMsg, 'ERROR', 4000);

    } finally {
      this.updateState({ isLoading: false });
    }
  }

  closeSendForApproval(): void {
    this.resetState();
  }

  private setupInputSubscription(): void {
    this.userControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.filterUsers(value || '');
    });
  }

  private async initializeComponent(): Promise<void> {
    const storedApprovalState = localStorage.getItem('isApproved') === 'true';
    this.updateState({ isApproved: storedApprovalState });
  }

  private filterUsers(value: string): void {
    const filterValue = value.toLowerCase().trim();
    const currentState = this.state$.value;

    const filtered = this.users.filter(user =>
      user.name.toLowerCase().includes(filterValue) &&
      !currentState.selectedUsers.some(selected => selected.id === user.id)
    );

    this.updateState({ filteredUsers: filtered });
  }

  private async fetchUsers(): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      const data = await this.userService.getUsers()
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      if (data) {
        this.users = data;
        this.filterUsers('');
      }
    } catch (error) {
      this.updateState({ error: 'Failed to load users' });
      console.log(error);
      
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  private handleSuccessfulSubmission(): void {
    this.toast.success('Approval sent successfully', 'SUCCESS', 4000);
    localStorage.setItem('isApproved', 'true');
    this.resetState();
    this.updateState({ isApproved: true });
  }

  private resetState(): void {
    this.updateState({
      ...INITIAL_STATE,
      isApproved: this.state$.value.isApproved
    });
    this.resetRichTextEditor = true;
  }

  private updateState(partialState: Partial<ApprovalState>): void {
    this.state$.next({
      ...this.state$.value,
      ...partialState
    });
  }
}